import { requireNonNullable } from '@disy/lib/custom-error';
import { getSetting } from '@disy/workbook.lib/settings';

import { MeasureAggregation } from 'cadenza/repository/model/measure-aggregation.gen';
import { getStore } from 'cadenza/store/cadenza-store';
import { AttributeModificationKey } from 'cadenza/workbook/api/attribute-modification-key';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import { CommonDataViewSettingsKey } from 'cadenza/workbook/model/common-data-view-settings-key.gen';
import { ConditionOperator, ConditionOperatori18nAsSymbols } from 'cadenza/workbook/model/condition-operator.gen';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import { getDataViewById } from 'cadenza/workbook/workbook-core-selectors.state';

import i18n from './rule-description.properties';

interface RuleConditionDescription {
  printName: string;
  aggregation?: MeasureAggregation;
  operator: ConditionOperator;
  formattedValue: string;
}

export function getDisplayRuleHintText(dataViewId: DataViewId) {
  const defaultText = i18n('hintText.default');
  const hintText =
    getSetting<string>(
      getDataViewById(getStore().getState(), dataViewId),
      CommonDataViewSettingsKey.toPersistenceKey('PRECONDITION_NOT_FULFILLED_MESSAGE'),
    ) ?? defaultText;
  return { defaultText, hintText };
}

export function createRuleConditionsPreview(
  attributes: Pick<WorkbookAttributeDto, 'printName' | 'settings' | 'status'>[],
) {
  const conditions = attributes.map((attribute) => {
    if (attribute.status !== 'OK') {
      return i18n('rule.condition.invalid');
    }
    const aggregation = MeasureAggregation.fromPersistenceKey(
      requireNonNullable(getSetting<MeasureAggregation>(attribute, AttributeModificationKey['MEASURE_AGGREGATION'])),
    );
    const operator = ConditionOperator.fromPersistenceKey(
      requireNonNullable(getSetting<ConditionOperator>(attribute, AttributeModificationKey['PRECONDITION_OPERATOR'])),
    );
    const formattedValue = requireNonNullable(
      getSetting<string>(attribute, AttributeModificationKey['PRECONDITION_VALUE']),
    );
    return convertRuleToString({
      printName: attribute.printName,
      aggregation,
      operator,
      formattedValue,
    });
  });
  return conditions.join(` ${i18n('rule.condition.separator')} `);
}

export function crateRuleDescription(ruleConditions: RuleConditionDescription[]) {
  return String(ruleConditions.map((rule) => convertRuleToString(rule)).join(` ${i18n('rule.condition.separator')} `));
}

function convertRuleToString({ aggregation, printName, operator, formattedValue }: RuleConditionDescription) {
  return aggregation ?
      `${MeasureAggregation.i18n(aggregation)} ${i18n('of')} "${printName}" ${ConditionOperatori18nAsSymbols(operator)} ${formattedValue}`
    : `"${printName}" ${ConditionOperatori18nAsSymbols(operator)} ${formattedValue}`;
}
